/**
 * UnifiedSessionListSidebar Component
 * 
 * Displays merged session list from both Subject Management AI and Reports AI.
 * Shows source badges and provides search/filter functionality.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Spin, Button, Input, Select } from 'antd';
import SessionListItem from '../../modules/reports/components/reports-ai-assistant/SessionListItem';

const { Search } = Input;
const { Option } = Select;

const UnifiedSessionListSidebar = ({
  userId,
  sessions = [],
  loading = false,
  error = null,
  warning = null,
  activeSessionId,
  onSelectSession,
  onCreateSession,
  onDeleteSession,
  onRenameSession
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [creating, setCreating] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);

  // Handle resize
  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = e.clientX;
      if (newWidth >= 240 && newWidth <= 600) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Filter sessions based on search and source filter
  const filteredSessions = useMemo(() => {
    return sessions.filter(session => {
      // Search filter
      const matchesSearch = !searchQuery || 
        session.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (session.id && session.id.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Source filter
      const matchesSource = sourceFilter === 'all' || session.source === sourceFilter;
      
      return matchesSearch && matchesSource;
    });
  }, [sessions, searchQuery, sourceFilter]);

  // Get source badge icon and color
  const getSourceBadge = (source) => {
    if (source === 'subject-management') {
      return { icon: '👥', color: '#4287f5', label: 'Subject' };
    }
    return { icon: '📊', color: '#52c41a', label: 'Analytics' };
  };

  // Handle create session
  const handleCreateSession = async () => {
    if (creating) return;
    
    setCreating(true);
    try {
      await onCreateSession();
    } catch (err) {
      console.error('Failed to create session:', err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="session-list-sidebar" style={{ width: `${sidebarWidth}px` }}>
      {/* Header */}
      <div className="session-list-header" style={{ paddingTop: '28px', paddingLeft: '24px', paddingRight: '24px', paddingBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#1a1a1a', paddingLeft: '8px' }}>AI Assistant</h2>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#8c8c8c',
              borderRadius: '4px',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            title={showFilters ? 'Hide filters' : 'Show filters'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10,18H14V16H10M3,6V8H21V6M6,13H18V11H6"/>
            </svg>
          </button>
        </div>
        <Button
          type="primary"
          onClick={handleCreateSession}
          loading={creating}
          disabled={creating}
          style={{ width: '100%' }}
          data-testid="ai-new-session"
        >
          New Session
        </Button>
      </div>

      {/* Search and Filter - Toggleable */}
      {showFilters && (
        <div className="session-filters" style={{ padding: '12px 16px', borderBottom: '1px solid #e8e8e8' }}>
          <Search
            placeholder="🔍 Search sessions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            style={{ marginBottom: '8px' }}
          />
          
          <Select
            value={sourceFilter}
            onChange={setSourceFilter}
            style={{ width: '100%' }}
          >
            <Option value="all">All Sessions</Option>
            <Option value="subject-management">👥 Subject Sessions</Option>
            <Option value="reports">📊 Analytics Sessions</Option>
          </Select>
        </div>
      )}

      {/* Error/Warning Messages */}
      {error && (
        <div className="session-list-error" style={{ 
          padding: '12px 16px', 
          background: '#fff1f0', 
          color: '#cf1322',
          borderBottom: '1px solid #ffccc7'
        }}>
          {error}
        </div>
      )}
      
      {warning && (
        <div className="session-list-warning" style={{ 
          padding: '12px 16px', 
          background: '#fffbe6', 
          color: '#d48806',
          borderBottom: '1px solid #ffe58f'
        }}>
          {warning}
        </div>
      )}

      {/* Session List */}
      <div className="session-list-content">
        {loading ? (
          <div className="session-list-loading" style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            padding: '40px 0' 
          }}>
            <Spin description="Loading sessions..." />
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="session-list-empty" style={{ 
            padding: '40px 16px', 
            textAlign: 'center', 
            color: '#8c8c8c' 
          }}>
            {searchQuery || sourceFilter !== 'all' 
              ? 'No sessions match your filters'
              : 'No sessions yet. Create your first session to get started!'}
          </div>
        ) : (
          <div className="session-list-items">
            {filteredSessions.map(session => {
              const sourceBadge = getSourceBadge(session.source);
              
              return (
                <SessionListItem
                  key={session.id}
                  session={session}
                  isActive={session.id === activeSessionId}
                  onSelect={() => onSelectSession(session.id)}
                  onDelete={() => onDeleteSession(session.id)}
                  onRename={(newTitle) => onRenameSession && onRenameSession(session.id, newTitle)}
                  sourceBadge={sourceBadge}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Resize handle */}
      <div
        className="session-list-sidebar__resize-handle"
        onMouseDown={handleMouseDown}
        style={{ cursor: isResizing ? 'col-resize' : 'col-resize' }}
      />
    </div>
  );
};

export default UnifiedSessionListSidebar;
